import { Currency } from '@texas/api/endpoints/currenciesApi';
import { useCallback, useEffect, useRef } from 'react';

export function useCurrencies({
  currencies,
}: {
  currencies: Currency[] | null;
}) {
  const normalizedCurrency = useRef<Record<
    number,
    Currency | undefined
  > | null>(null);

  const findCurrency = useCallback(
    (id: number) => {
      return currencies?.find((currency) => currency.id === id);
    },
    [currencies],
  );

  useEffect(() => {
    if (currencies) {
      const branchCurrencies: Record<string, number> = {};
      const branches: Record<number, boolean> = {};
      for (let i = 0; i < currencies.length; i++) {
        if (!branches[currencies[i].branchId]) {
          branches[currencies[i].branchId] = true;
        }

        branchCurrencies[currencies[i].code] =
          (branchCurrencies[currencies[i].code] || 0) + 1;
      }

      const currencyCodeKeys = Object.keys(branchCurrencies);
      const totalBranches = Object.keys(branches).length;
      for (let i = 0; i < currencyCodeKeys.length; i++) {
        if (branchCurrencies[currencyCodeKeys[i]] === totalBranches) {
          const c = currencies.filter((c) => c.code === currencyCodeKeys[i]);

          normalizedCurrency.current = Object.fromEntries(
            c.map((x) => [x.branchId, x]),
          );
        }
      }
    }
  }, [currencies]);

  const convertCurrencyRate = useCallback(
    (fromCurrencyId: number, toCurrencyId: number, dir: 'buy' | 'sell') => {
      if (fromCurrencyId === toCurrencyId) {
        return 1;
      }

      const fromCurrency = findCurrency(fromCurrencyId);
      const toCurrency = findCurrency(toCurrencyId);

      if (!fromCurrency || !toCurrency || !normalizedCurrency.current) {
        return dir == 'buy' ? toCurrency?.buyingRate : toCurrency?.sellingRate;
      }

      const normalizedFromCurrency =
        normalizedCurrency.current[fromCurrency.branchId];
      const normalizedToCurrency =
        normalizedCurrency.current[toCurrency.branchId];

      if (!normalizedFromCurrency || !normalizedToCurrency) {
        return dir == 'buy' ? toCurrency.buyingRate : toCurrency.sellingRate;
      }

      if (dir == 'buy') {
        return (
          (toCurrency.buyingRate * normalizedToCurrency.buyingRate) /
          (fromCurrency.buyingRate * normalizedFromCurrency.buyingRate)
        );
      }

      return (
        (fromCurrency.sellingRate * normalizedFromCurrency.sellingRate) /
        (toCurrency.sellingRate * normalizedToCurrency.sellingRate)
      );
    },
    [findCurrency],
  );

  const convertCurrencyValue = useCallback(
    (
      fromCurrencyId: number,
      toCurrencyId: number,
      amount: number,
      dir: 'buy' | 'sell',
    ) => {
      if (fromCurrencyId === toCurrencyId) {
        return amount;
      }

      const fromCurrency = findCurrency(fromCurrencyId);
      const toCurrency = findCurrency(toCurrencyId);

      if (!fromCurrency || !toCurrency || !normalizedCurrency.current) {
        return amount;
      }

      const normalizedFromCurrency =
        normalizedCurrency.current[fromCurrency.branchId];
      const normalizedToCurrency =
        normalizedCurrency.current[toCurrency.branchId];

      if (!normalizedFromCurrency || !normalizedToCurrency) {
        return amount;
      }

      const conversionRate = convertCurrencyRate(
        fromCurrencyId,
        toCurrencyId,
        dir,
      );
      return conversionRate == null ? amount : amount * conversionRate;
    },
    [convertCurrencyRate, findCurrency],
  );

  const currenciesInBranch = useCallback(
    (branchId: number) => {
      return (
        currencies?.filter((currency) => currency.branchId === branchId) ?? []
      );
    },
    [currencies],
  );

  return {
    convertCurrencyRate,
    findCurrency,
    currenciesInBranch,
    convertCurrencyValue,
  };
}
